import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { isAdminLike } from '../utils/auth';

const API_URL = getApiUrl();

const statusOptions = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'terminated', label: 'Desligado' },
  { value: 'deleted', label: 'Arquivado' },
];

const contractTypeOptions = [
  { value: 'prazo_indeterminado', label: 'Prazo indeterminado' },
  { value: 'experiencia', label: 'Experiência' },
  { value: 'prazo_determinado', label: 'Prazo determinado' },
];

const scaleOptions = [
  { value: '5x2', label: '5x2' },
  { value: '6x1', label: '6x1' },
];

const salaryFrequencyOptions = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'semanal', label: 'Semanal' },
];

const accountTypeOptions = [
  { value: '', label: 'Não informado' },
  { value: 'corrente', label: 'Conta corrente' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'salario', label: 'Conta salário' },
];

const dayOffOptions = [
  'sábado e domingo',
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

const numericFields = new Set([
  'trialPeriodDays',
  'trialExtensionDays',
  'salaryBrl',
  'paymentDay',
  'weeklyHours',
  'mealAllowanceBrl',
  'foodAllowanceBrl',
]);

const booleanFields = new Set([
  'hasDisability',
  'transportVoucherEnabled',
  'healthPlanEnabled',
  'epiRequired',
]);

const jsonFields = new Set(['workScheduleJson', 'benefitsJson']);

const fieldGroups = [
  {
    id: 'pessoais',
    label: 'Pessoais',
    fields: [
      { name: 'status', label: 'Status', type: 'select', options: statusOptions },
      { name: 'fullName', label: 'Nome completo', required: true },
      { name: 'preferredName', label: 'Nome social/apelido' },
      { name: 'cpf', label: 'CPF', required: true },
      { name: 'rg', label: 'RG' },
      { name: 'rgIssuer', label: 'Órgão emissor' },
      { name: 'rgUf', label: 'UF do RG' },
      { name: 'birthDate', label: 'Nascimento', type: 'date' },
      { name: 'motherName', label: 'Nome da mãe' },
      { name: 'fatherName', label: 'Nome do pai' },
      { name: 'nationality', label: 'Nacionalidade' },
      { name: 'birthCity', label: 'Cidade de nascimento' },
      { name: 'birthState', label: 'UF de nascimento' },
      { name: 'maritalStatus', label: 'Estado civil' },
      { name: 'educationLevel', label: 'Escolaridade' },
      { name: 'gender', label: 'Gênero' },
      { name: 'raceColor', label: 'Raça/cor' },
      { name: 'hasDisability', label: 'Pessoa com deficiência', type: 'checkbox' },
      { name: 'disabilityDescription', label: 'Descrição da deficiência', multiline: true },
    ],
  },
  {
    id: 'contato',
    label: 'Contato',
    fields: [
      { name: 'phone', label: 'Telefone' },
      { name: 'email', label: 'E-mail' },
      { name: 'addressCep', label: 'CEP' },
      { name: 'addressStreet', label: 'Rua' },
      { name: 'addressNumber', label: 'Número' },
      { name: 'addressComplement', label: 'Complemento' },
      { name: 'addressNeighborhood', label: 'Bairro' },
      { name: 'addressCity', label: 'Cidade' },
      { name: 'addressState', label: 'UF' },
    ],
  },
  {
    id: 'documentos',
    label: 'Documentos',
    fields: [
      { name: 'ctpsNumber', label: 'CTPS número' },
      { name: 'ctpsSeries', label: 'CTPS série' },
      { name: 'ctpsUf', label: 'CTPS UF' },
      { name: 'nisPisPasep', label: 'NIS/PIS/PASEP' },
      { name: 'voterRegistration', label: 'Título de eleitor' },
      { name: 'militaryCertificate', label: 'Certificado militar' },
      { name: 'cnhNumber', label: 'CNH' },
    ],
  },
  {
    id: 'contrato',
    label: 'Contrato',
    fields: [
      { name: 'admissionDate', label: 'Data de admissão', type: 'date', required: true },
      { name: 'registrationNumber', label: 'Matrícula' },
      { name: 'contractType', label: 'Tipo de contrato', type: 'select', options: contractTypeOptions },
      { name: 'trialPeriodDays', label: 'Dias de experiência', type: 'number' },
      { name: 'trialExtensionDays', label: 'Prorrogação da experiência', type: 'number' },
      { name: 'jobTitle', label: 'Cargo', required: true },
      { name: 'cbo', label: 'CBO' },
      { name: 'department', label: 'Departamento' },
      { name: 'workLocation', label: 'Local de trabalho' },
      { name: 'supervisorName', label: 'Supervisor' },
      { name: 'salaryBrl', label: 'Salário', type: 'number', required: true },
      { name: 'salaryPaymentFrequency', label: 'Periodicidade', type: 'select', options: salaryFrequencyOptions },
      { name: 'paymentDay', label: 'Dia de pagamento', type: 'number' },
    ],
  },
  {
    id: 'jornada',
    label: 'Jornada',
    fields: [
      { name: 'workScheduleType', label: 'Escala', type: 'select', options: scaleOptions, required: true },
      { name: 'weeklyHours', label: 'Horas semanais', type: 'number' },
      { name: 'workStartTime', label: 'Entrada', type: 'time', required: true },
      { name: 'workEndTime', label: 'Saída', type: 'time', required: true },
      { name: 'breakStartTime', label: 'Início intervalo', type: 'time', required: true },
      { name: 'breakEndTime', label: 'Fim intervalo', type: 'time', required: true },
      { name: 'weeklyDayOff', label: 'Folga semanal', type: 'select', options: dayOffOptions.map((value) => ({ value, label: value })) },
      { name: 'workScheduleJson', label: 'Horários por dia (JSON)', multiline: true },
    ],
  },
  {
    id: 'financeiro',
    label: 'Pagamento',
    fields: [
      { name: 'bankName', label: 'Banco' },
      { name: 'bankAgency', label: 'Agência' },
      { name: 'bankAccount', label: 'Conta' },
      { name: 'bankAccountType', label: 'Tipo de conta', type: 'select', options: accountTypeOptions },
      { name: 'pixKey', label: 'Chave Pix' },
      { name: 'transportVoucherEnabled', label: 'Vale-transporte', type: 'checkbox' },
      { name: 'mealAllowanceBrl', label: 'Auxílio refeição', type: 'number' },
      { name: 'foodAllowanceBrl', label: 'Auxílio alimentação', type: 'number' },
      { name: 'healthPlanEnabled', label: 'Plano de saúde', type: 'checkbox' },
      { name: 'benefitsJson', label: 'Benefícios adicionais (JSON)', multiline: true },
    ],
  },
  {
    id: 'saude',
    label: 'Saúde',
    fields: [
      { name: 'asoAdmissionDate', label: 'ASO admissional', type: 'date' },
      { name: 'asoExpirationDate', label: 'Validade do ASO', type: 'date' },
      { name: 'epiRequired', label: 'EPI obrigatório', type: 'checkbox' },
      { name: 'notes', label: 'Observações', multiline: true, fullWidth: true },
    ],
  },
];

const allFieldNames = fieldGroups.flatMap((group) => group.fields.map((field) => field.name));

const createEmptyForm = () => ({
  ...allFieldNames.reduce((accumulator, fieldName) => {
    if (accumulator[fieldName] !== undefined) return accumulator;
    accumulator[fieldName] = booleanFields.has(fieldName) ? false : '';
    return accumulator;
  }, {}),
  status: 'active',
  contractType: 'prazo_indeterminado',
  workScheduleType: '5x2',
  salaryPaymentFrequency: 'mensal',
  weeklyDayOff: 'sábado e domingo',
});

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return 'Não informado';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (value) => {
  if (!value) return 'Sem data';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const normalizeFormFromEmployee = (employee = {}) => {
  const base = createEmptyForm();
  allFieldNames.forEach((fieldName) => {
    const value = employee[fieldName];
    if (jsonFields.has(fieldName)) {
      base[fieldName] = value ? JSON.stringify(value, null, 2) : '';
    } else if (booleanFields.has(fieldName)) {
      base[fieldName] = Boolean(value);
    } else {
      base[fieldName] = value ?? '';
    }
  });
  return base;
};

const buildEmployeePayload = (form) => {
  const payload = {};
  allFieldNames.forEach((fieldName) => {
    const value = form[fieldName];
    if (jsonFields.has(fieldName)) {
      if (!String(value || '').trim()) {
        payload[fieldName] = null;
        return;
      }
      payload[fieldName] = JSON.parse(value);
      return;
    }
    if (numericFields.has(fieldName)) {
      payload[fieldName] = value === '' || value === null || value === undefined ? null : Number(value);
      return;
    }
    if (booleanFields.has(fieldName)) {
      payload[fieldName] = Boolean(value);
      return;
    }
    payload[fieldName] = value;
  });
  return payload;
};

const hasContractMinimumData = (form) => (
  Boolean(String(form.fullName || '').trim())
  && Boolean(String(form.cpf || '').trim())
  && Boolean(String(form.salaryBrl || '').trim())
  && Boolean(String(form.admissionDate || '').trim())
  && Boolean(String(form.jobTitle || '').trim())
  && Boolean(String(form.workScheduleType || '').trim())
  && Boolean(String(form.workStartTime || '').trim())
  && Boolean(String(form.workEndTime || '').trim())
  && Boolean(String(form.breakStartTime || '').trim())
  && Boolean(String(form.breakEndTime || '').trim())
);

const extractApiError = async (response, fallbackMessage) => {
  const data = await response.json().catch(() => ({}));
  return data.error || fallbackMessage;
};

const Funcionarios = () => {
  const auth = useAuth();
  const [query, setQuery] = useState('');
  const [employees, setEmployees] = useState([]);
  const [permissions, setPermissions] = useState({
    canManage: isAdminLike(auth.user),
    canGenerateContracts: isAdminLike(auth.user),
    canViewSensitive: isAdminLike(auth.user),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', employeeId: null });
  const [formTab, setFormTab] = useState('pessoais');
  const [form, setForm] = useState(createEmptyForm);
  const [formError, setFormError] = useState('');
  const [formWarnings, setFormWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastContracts, setLastContracts] = useState([]);
  const [contractPreview, setContractPreview] = useState({ open: false, contract: null, warnings: [] });

  const canManage = permissions.canManage && isAdminLike(auth.user);
  const canGenerateContracts = permissions.canGenerateContracts && isAdminLike(auth.user);

  const loadEmployees = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/employees?q=${encodeURIComponent(q || '')}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar funcionários.');
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
      setPermissions((prev) => ({ ...prev, ...(data.permissions || {}) }));
      setError('');
    } catch (err) {
      setError(err.message || 'Falha ao carregar funcionários.');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => loadEmployees(query), 250);
    return () => window.clearTimeout(timer);
  }, [loadEmployees, query]);

  const openCreate = () => {
    setDialog({ open: true, mode: 'create', employeeId: null });
    setForm(createEmptyForm());
    setFormTab('pessoais');
    setFormError('');
    setFormWarnings([]);
    setLastContracts([]);
  };

  const loadContracts = async (employeeId) => {
    if (!employeeId || !canGenerateContracts) {
      setLastContracts([]);
      return;
    }
    try {
      const response = await apiFetch(`${API_URL}/api/employees/${employeeId}/contracts`);
      const data = await response.json().catch(() => ({}));
      if (response.ok) setLastContracts(Array.isArray(data.contracts) ? data.contracts : []);
    } catch {
      setLastContracts([]);
    }
  };

  const openEdit = async (employee) => {
    setDialog({ open: true, mode: 'edit', employeeId: employee.id });
    setFormTab('pessoais');
    setFormError('');
    setFormWarnings([]);
    setLastContracts([]);
    try {
      const response = await apiFetch(`${API_URL}/api/employees/${employee.id}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar funcionário.');
      setForm(normalizeFormFromEmployee(data.employee || {}));
      setFormWarnings(data.readiness?.warnings || []);
      loadContracts(employee.id);
    } catch (err) {
      setFormError(err.message || 'Falha ao carregar funcionário.');
      setForm(normalizeFormFromEmployee(employee));
    }
  };

  const closeDialog = () => {
    if (saving || archiving || generating) return;
    setDialog({ open: false, mode: 'create', employeeId: null });
    setForm(createEmptyForm());
    setFormError('');
    setFormWarnings([]);
    setLastContracts([]);
  };

  const handleFormChange = (fieldName) => (event) => {
    const value = booleanFields.has(fieldName) ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [fieldName]: value }));
  };

  const saveEmployee = async () => {
    if (!canManage) return;
    setSaving(true);
    setFormError('');
    try {
      const payload = buildEmployeePayload(form);
      const isEdit = dialog.mode === 'edit' && dialog.employeeId;
      const response = await apiFetch(
        isEdit ? `${API_URL}/api/employees/${dialog.employeeId}` : `${API_URL}/api/employees`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar funcionário.');
      setForm(normalizeFormFromEmployee(data.employee));
      setFormWarnings(data.warnings || []);
      setDialog({ open: true, mode: 'edit', employeeId: data.employee.id });
      await loadEmployees(query);
    } catch (err) {
      setFormError(err.message || 'Falha ao salvar funcionário.');
    } finally {
      setSaving(false);
    }
  };

  const archiveEmployee = async () => {
    if (!canManage || !dialog.employeeId) return;
    if (!window.confirm('Arquivar este funcionário?')) return;
    setArchiving(true);
    setFormError('');
    try {
      const response = await apiFetch(`${API_URL}/api/employees/${dialog.employeeId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await extractApiError(response, 'Falha ao arquivar funcionário.'));
      closeDialog();
      await loadEmployees(query);
    } catch (err) {
      setFormError(err.message || 'Falha ao arquivar funcionário.');
    } finally {
      setArchiving(false);
    }
  };

  const generateContract = async () => {
    if (!canGenerateContracts || !dialog.employeeId) return;
    setGenerating(true);
    setFormError('');
    try {
      const response = await apiFetch(`${API_URL}/api/employees/${dialog.employeeId}/contracts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const details = Array.isArray(data.errors) && data.errors.length ? ` ${data.errors.join(' ')}` : '';
        throw new Error(`${data.error || 'Falha ao gerar contrato.'}${details}`);
      }
      setContractPreview({ open: true, contract: data.contract, warnings: data.warnings || [] });
      setLastContracts((prev) => [data.contract, ...prev]);
    } catch (err) {
      setFormError(err.message || 'Falha ao gerar contrato.');
    } finally {
      setGenerating(false);
    }
  };

  const printContract = () => {
    const body = contractPreview.contract?.renderedBody || '';
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Contrato de trabalho</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.55; padding: 32px; color: #111827; }
            pre { white-space: pre-wrap; font-family: inherit; }
          </style>
        </head>
        <body><pre>${escapeHtml(body)}</pre></body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const downloadContractHtml = () => {
    const body = contractPreview.contract?.renderedBody || '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Contrato de trabalho</title></head><body><pre style="white-space:pre-wrap;font-family:Arial,sans-serif;line-height:1.55;">${escapeHtml(body)}</pre></body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrato-funcionario-${dialog.employeeId || 'novo'}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderField = (field) => {
    if (field.type === 'checkbox') {
      return (
        <FormControlLabel
          key={field.name}
          control={<Switch checked={Boolean(form[field.name])} onChange={handleFormChange(field.name)} disabled={!canManage} />}
          label={field.label}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <FormControl key={field.name} fullWidth size="small">
          <InputLabel>{field.label}</InputLabel>
          <Select
            label={field.label}
            value={form[field.name] ?? ''}
            onChange={handleFormChange(field.name)}
            disabled={!canManage}
          >
            {(field.options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        key={field.name}
        label={field.label}
        value={form[field.name] ?? ''}
        onChange={handleFormChange(field.name)}
        type={field.type || 'text'}
        size="small"
        fullWidth
        required={Boolean(field.required)}
        multiline={Boolean(field.multiline)}
        minRows={field.multiline ? 3 : undefined}
        disabled={!canManage}
        InputLabelProps={['date', 'time'].includes(field.type) ? { shrink: true } : undefined}
      />
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      p: { xs: 1.5, md: 2 },
      background: 'linear-gradient(135deg, #f8fafc 0%, #eef6f3 52%, #fff7ed 100%)',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} mb={1.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PageMenuButton />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#102033' }}>Funcionários</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Cadastro, jornada e contratos de trabalho por empresa.
          </Typography>
        </Box>
        {canManage ? (
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openCreate}>
            Cadastrar
          </Button>
        ) : null}
      </Stack>

      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nome, CPF, cargo, telefone ou status"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seu perfil pode consultar informações permitidas. Salários, documentos completos e contratos ficam restritos aos perfis autorizados.
        </Alert>
      ) : null}

      {loading ? (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CircularProgress size={22} />
          <Typography>Carregando funcionários...</Typography>
        </Stack>
      ) : null}

      {!loading && !employees.length ? (
        <Alert severity="info">Nenhum funcionário encontrado.</Alert>
      ) : null}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 2,
      }}>
        {employees.map((employee) => (
          <Card
            key={employee.id}
            onClick={() => openEdit(employee)}
            sx={{
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid #e5e7eb',
              boxShadow: '0 16px 32px -28px rgba(15, 23, 42, 0.5)',
              '&:hover': { borderColor: '#0f766e' },
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }} noWrap>
                      {employee.preferredName || employee.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {employee.fullName}
                    </Typography>
                  </Box>
                  <Chip size="small" label={statusOptions.find((item) => item.value === employee.status)?.label || employee.status} />
                </Stack>
                <Divider />
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WorkOutlineOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2">{employee.jobTitle || 'Cargo não informado'}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BadgeOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2">CPF {employee.cpfMasked || 'não informado'}</Typography>
                  </Stack>
                  <Typography variant="body2">
                    {employee.salaryVisible ? formatCurrency(employee.salaryBrl) : 'Salário restrito'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {employee.workScheduleType || 'Escala não informada'} · {employee.workStartTime || '--:--'} às {employee.workEndTime || '--:--'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Admissão: {formatDate(employee.admissionDate)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialog.mode === 'edit' ? 'Editar funcionário' : 'Cadastrar funcionário'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {formError ? <Alert severity="error">{formError}</Alert> : null}
            {formWarnings.length ? (
              <Alert severity="warning">
                {formWarnings.map((warning) => (
                  <Typography key={warning} variant="body2">{warning}</Typography>
                ))}
              </Alert>
            ) : null}
            <Tabs value={formTab} onChange={(_, value) => setFormTab(value)} variant="scrollable" scrollButtons="auto">
              {fieldGroups.map((group) => (
                <Tab key={group.id} value={group.id} label={group.label} />
              ))}
            </Tabs>
            {fieldGroups.map((group) => (
              group.id === formTab ? (
                <Box key={group.id} sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 2,
                }}>
                  {group.fields.map((field) => (
                    <Box key={field.name} sx={{ gridColumn: field.fullWidth || field.multiline ? { xs: 'auto', md: '1 / -1' } : 'auto' }}>
                      {renderField(field)}
                    </Box>
                  ))}
                </Box>
              ) : null
            ))}
            {lastContracts.length ? (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">Contratos gerados</Typography>
                    {lastContracts.slice(0, 3).map((contract) => (
                      <Stack key={contract.id} direction="row" alignItems="center" spacing={1}>
                        <DescriptionOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          Versão {contract.templateVersion} · {new Date(contract.generatedAt).toLocaleString('pt-BR')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {dialog.mode === 'edit' && canManage ? (
            <Button color="error" startIcon={<ArchiveOutlinedIcon />} onClick={archiveEmployee} disabled={archiving}>
              {archiving ? 'Arquivando...' : 'Arquivar'}
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={closeDialog}>Fechar</Button>
          {canGenerateContracts ? (
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={generateContract}
              disabled={generating || !dialog.employeeId || !hasContractMinimumData(form)}
            >
              {generating ? 'Gerando...' : 'Gerar Contrato'}
            </Button>
          ) : null}
          {canManage ? (
            <Button variant="contained" onClick={saveEmployee} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={contractPreview.open} onClose={() => setContractPreview({ open: false, contract: null, warnings: [] })} maxWidth="md" fullWidth>
        <DialogTitle>Contrato gerado</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {contractPreview.warnings.length ? (
              <Alert severity="warning">
                {contractPreview.warnings.map((warning) => (
                  <Typography key={warning} variant="body2">{warning}</Typography>
                ))}
              </Alert>
            ) : null}
            <Box sx={{
              p: 2,
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              bgcolor: '#ffffff',
              maxHeight: '62vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.6,
            }}>
              {contractPreview.contract?.renderedBody || ''}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadOutlinedIcon />} onClick={downloadContractHtml}>
            Baixar HTML
          </Button>
          <Button startIcon={<PrintOutlinedIcon />} onClick={printContract}>
            Imprimir
          </Button>
          <Button onClick={() => setContractPreview({ open: false, contract: null, warnings: [] })}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Funcionarios;
